import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { AppDispatch } from '@/store'
import Api from '@/services/http'
import { addressUrl } from '@/services/url'
import type * as Types from './types'
import { getSupportAutoSplitCountryList } from '@/services/api'
import { lockPromiseFn } from '@/utils/lockPromise'

const AddressInputHandler = {
  get: function (target, name) {
    return target.hasOwnProperty(name)
      ? target[name]
      : {
          provinceVisibility: true,
          cityVisibility: true,
          districtVisibility: true,
        }
  },
}

const initialState: Types.IInitialState = {
  areaList: null,
  areaListMap: {},
  verifyFieldMap: {},
  addressConfig: new Proxy({}, AddressInputHandler),
  splitSupportCountryMap: {},
}

const addressSlice = createSlice({
  name: 'address',
  initialState: initialState,
  reducers: {
    setAreaList(state, action: PayloadAction<Types.Area[]>) {
      return {
        ...state,
        areaList: action.payload,
      }
    },
    setAreaListMap(
      state,
      action: PayloadAction<{
        key: string
        value: Types.Area[]
      }>
    ) {
      const { key, value } = action.payload
      return {
        ...state,
        areaListMap: {
          ...state.areaListMap,
          [key]: value,
        },
      }
    },
    setAddressVerifyMap(state, action: PayloadAction<Record<string, Types.VerifyFieldType>>) {
      return {
        ...state,
        verifyFieldMap: action.payload,
      }
    },
    setAddressConfig(state, action: PayloadAction<Record<string, Types.CountryConfigType>>) {
      return {
        ...state,
        addressConfig: action.payload,
      }
    },
    setSplitSupportCountryMap(state, action: PayloadAction<Types.IInitialState['splitSupportCountryMap']>) {
      return {
        ...state,
        splitSupportCountryMap: action.payload,
      }
    },
  },
})

export const { setAreaList, setAreaListMap, setAddressVerifyMap, setAddressConfig, setSplitSupportCountryMap } = addressSlice.actions

/** 获取省市区信息 */
export const getAreaList = (params?: Types.GetAreaParams) => async (dispatch: AppDispatch) => {
  const getAddressList = lockPromiseFn(Api.admin.get)
  const response = await getAddressList('/api/addresses/list', { params })
  const { data, code, message } = response
  if (code === 'SUCCESS') {
    const { nodes: areas, language } = data as unknown as { nodes: Types.Area[]; language: string }
    dispatch(setAreaList(areas))
    dispatch(setAreaListMap({ key: params.countryCode, value: areas }))

    return { areas, language }
  }

  return Promise.reject({ code, message })
}

/**
 * 国家地区的省市区是否显示的配置
 * @returns
 */
export const addressConfigCreator = () => async (dispatch: AppDispatch) => {
  const response = await Api.admin.post<Types.AddressConfigType>(addressUrl.addressConfig)

  const { code, data } = response
  const { configs, filedConfigs } = data || {}
  if (code === 'SUCCESS') {
    // 地址可见性配置: 配置没有该国家，省市区默认展示
    if (Array.isArray(configs)) {
      const addressConfig = configs?.reduce((sum, next) => {
        sum[next.countryCode] = next
        return sum
      }, new Proxy({}, AddressInputHandler))

      dispatch(setAddressConfig(addressConfig))
    }
    // 地址必填配置,配置没有该国家，省市必填
    const handler = {
      get: function (target, name) {
        return target.hasOwnProperty(name) ? target[name] : { province: '1', city: '1' }
      },
    }
    const emptyObj = {}
    const obj = new Proxy(emptyObj, handler)
    const vertifyMap = filedConfigs?.reduce((sum, { countryCode, data }) => {
      let map = {}
      try {
        map = JSON.parse(data)
      } catch (error) {}

      sum[countryCode] = map
      return sum
    }, obj)
    dispatch(setAddressVerifyMap(vertifyMap))
  }
}
/**
 * 获取支持地址智能拆分的国家
 * @returns
 */
export const splitSupportCountryMapCreator = () => async (dispatch: AppDispatch) => {
  const data = await getSupportAutoSplitCountryList()
  const map = data
    ? data.reduce((prev, current) => {
        const { enabled, countryCode } = current
        prev[countryCode] = enabled
        return prev
      }, {})
    : {}

  dispatch(setSplitSupportCountryMap(map))
}
export default addressSlice.reducer
